import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingBag, Star, ChevronLeft, Plus, Minus, Check, ChevronDown } from 'lucide-react';
import { testimonials } from '../data/products';
import { useCart } from '../context/CartContext';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../hooks/useProducts';

export default function ProductDetail() {
  const { id } = useParams();
  const { products, loading } = useProducts();
  const product = products.find(p => p.id === Number(id));
  const { addToCart } = useCart();

  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [openAccordion, setOpenAccordion] = useState('desc');

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
    setQty(1);
    setAdded(false);
  }, [id]);

  if (loading) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center">
        <p className="font-light text-gray-400">Loading product...</p>
      </main>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="font-display text-3xl font-light mb-4">Product not found</p>
        <Link to="/shop" className="text-xs tracking-widest uppercase border-b border-black">
          Back to Shop
        </Link>
      </div>
    );
  }

  const handleAdd = () => {
    addToCart(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const discount = Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100);
  const related = products.filter(p => p.id !== product.id && p.concern.some(c => product.concern.includes(c))).slice(0, 3);

  const accordions = [
    { key: 'desc', label: 'Description', content: product.description },
    {
      key: 'benefits', label: 'Benefits',
      content: product.benefits && product.benefits.length > 0 ? (
        <ul className="space-y-2">
          {product.benefits.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <span className="text-green-500 mt-0.5">✓</span> {b}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500">No benefits listed yet.</p>
      )
    },
    {
      key: 'how', label: 'How to Use',
      content: Array.isArray(product.howToUse) ? (
        <ol className="space-y-3 list-none">
          {product.howToUse.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center text-xs font-medium">
                {i + 1}
              </span>
              <span className="pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-sm text-gray-500">{product.howToUse || 'Please refer to the product packaging for complete usage instructions.'}</p>
      )
    },
    {
      key: 'suitable', label: 'Suitable For',
      content: product.suitableFor && product.suitableFor.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {product.suitableFor.map((s, i) => (
            <span key={i} className="text-xs border border-gray-200 px-3 py-1.5 rounded-full text-gray-600 bg-gray-50">
              {s}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-500">Suitable for most skin types.</p>
      )
    },
    {
      key: 'ing', label: 'Key Ingredients',
      content: (
        <ul className="space-y-2">
          {product.ingredients.map(i => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <span className="text-[#cc7c66] mt-0.5">·</span> {i}
            </li>
          ))}
        </ul>
      )
    },
  ];

  return (
    <main className="min-h-screen">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-6 pb-2">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Link to="/" className="hover:text-black transition-colors">Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-black transition-colors">Shop</Link>
          <span>/</span>
          <span className="text-black">{product.name}</span>
        </div>
      </div>

      {/* Main product section */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Images */}
          <div className="flex gap-4">
            {/* Thumbnails */}
            {product.images.length > 1 && (
              <div className="flex flex-col gap-3 w-16 flex-shrink-0">
                {product.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square overflow-hidden border-2 transition-colors ${activeImg === i ? 'border-black' : 'border-transparent'}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {/* Main image */}
            <div className="flex-1 aspect-square overflow-hidden bg-[#ffffff]">
              <img
                src={product.images[activeImg]}
                alt={product.name}
                className="w-full h-full object-contain transition-opacity duration-300"
                loading="eager"
              />
            </div>
          </div>

          {/* Info */}
          <div className="flex flex-col">
            {product.badge && (
              <span className={`self-start text-[10px] tracking-[0.2em] uppercase px-3 py-1 mb-4 font-medium ${
                product.badge === 'Bestseller' ? 'bg-black text-white' :
                product.badge === 'New' ? 'bg-[#f5e6e0] text-black' :
                'bg-[#cc7c66] text-white'
              }`}>{product.badge}</span>
            )}

            <h1 className="font-display text-4xl sm:text-5xl font-light leading-tight">{product.name}</h1>

            {/* Rating */}
            <div className="flex items-center gap-2 mt-4">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={13} className={i < Math.floor(product.rating) ? 'fill-black text-black' : 'fill-gray-200 text-gray-200'} />
                ))}
              </div>
              <span className="text-sm font-medium">{product.rating}</span>
              <span className="text-sm text-gray-400">({product.reviews} reviews)</span>
            </div>

            {/* Price */}
            <div className="flex items-center gap-3 mt-5">
              <span className="text-2xl font-medium">₹{product.price.toLocaleString()}</span>
              <span className="text-gray-400 line-through text-base">₹{product.originalPrice.toLocaleString()}</span>
              <span className="text-[#cc7c66] text-sm font-medium">{discount}% off</span>
            </div>

            <p className="text-xs text-gray-400 mt-1">Inclusive of all taxes · Free shipping above ₹499</p>

            {/* Concerns */}
            <div className="flex flex-wrap gap-2 mt-5">
              {product.concern.map(c => (
                <span key={c} className="text-xs border border-gray-200 px-3 py-1 rounded-full text-gray-500">
                  {c}
                </span>
              ))}
            </div>

            {/* Qty + Add */}
            <div className="flex items-center gap-4 mt-8">
              <div className="flex items-center border border-gray-300">
                <button
                  onClick={() => setQty(q => Math.max(1, q - 1))}
                  className="px-3 py-3 text-gray-500 hover:text-black transition-colors"
                >
                  <Minus size={14} />
                </button>
                <span className="px-5 text-sm font-medium">{qty}</span>
                <button
                  onClick={() => setQty(q => q + 1)}
                  className="px-3 py-3 text-gray-500 hover:text-black transition-colors"
                >
                  <Plus size={14} />
                </button>
              </div>
              <button
                onClick={handleAdd}
                className={`flex-1 flex items-center justify-center gap-2 py-4 text-xs tracking-[0.2em] uppercase font-medium transition-all duration-300 ${
                  added ? 'bg-green-600 text-white' : 'bg-black text-white hover:bg-gray-800'
                }`}
              >
                {added ? <><Check size={14} /> Added to Bag</> : <><ShoppingBag size={14} /> Add to Bag</>}
              </button>
            </div>

            {/* Size info */}
            <p className="text-xs text-gray-400 mt-3">Size: {product.size}</p>

            {/* Accordion */}
            <div className="mt-8 border-t border-gray-100">
              {accordions.map(acc => (
                <div key={acc.key} className="border-b border-gray-100">
                  <button
                    onClick={() => setOpenAccordion(openAccordion === acc.key ? null : acc.key)}
                    className="w-full flex items-center justify-between py-4 text-sm font-medium hover:text-gray-600 transition-colors"
                  >
                    {acc.label}
                    <ChevronDown size={15} className={`transition-transform text-gray-400 ${openAccordion === acc.key ? 'rotate-180' : ''}`} />
                  </button>
                  {openAccordion === acc.key && (
                    <div className="pb-5 text-sm text-gray-600 leading-relaxed font-light">
                      {typeof acc.content === 'string' ? <p>{acc.content}</p> : acc.content}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 border-t border-gray-100">
        <h2 className="font-display text-4xl font-light mb-10">Customer Reviews</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map(t => (
            <div key={t.id} className="p-6 bg-[#fdf8f6]">
              <div className="flex items-center gap-0.5 mb-3">
                {[...Array(t.rating)].map((_, i) => <Star key={i} size={12} className="fill-black text-black" />)}
              </div>
              <p className="font-display text-lg font-light italic text-gray-700 leading-relaxed">"{t.text}"</p>
              <div className="flex items-center gap-2.5 mt-4">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-xs font-medium text-[#cc7c66] border border-[#eecfc4]">
                  {t.avatar}
                </div>
                <div>
                  <p className="text-xs font-medium">{t.name}</p>
                  <p className="text-xs text-gray-400">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16 border-t border-gray-100">
          <h2 className="font-display text-4xl font-light mb-10">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </main>
  );
}
